/**
 * **舞台の設定の値 → 絵の当て方**（Issue #766 で `fxScene.js` から分けた。**描く側**）。
 *
 * `fxScene.js`（台本を作る層）が出す「そのフレームの舞台の設定」（`frame.states.fx_scene`）を、
 * カメラのショット・舞台全体 / 盤の側の CSS の filter に写す。台本と描く層の口（`pages/useDuelPicture.js`）の向こう側。
 */
import { SHOTS } from '../duelEditor/stage/camera';

const num = (v, def = 0) => (Number.isFinite(v) ? v : def);

/** 色の調整 → 舞台全体に当てる CSS の filter（素のままなら ''） */
export function gradeFilter(g) {
  if (!g) return '';
  const parts = [];
  if (g.brightness !== 1) parts.push(`brightness(${g.brightness})`);
  if (g.contrast !== 1) parts.push(`contrast(${g.contrast})`);
  if (g.saturate !== 1) parts.push(`saturate(${g.saturate})`);
  if (g.grayscale) parts.push(`grayscale(${g.grayscale})`);
  if (g.invert) parts.push(`invert(${g.invert})`);
  if (g.hue) parts.push(`hue-rotate(${g.hue}deg)`);
  return parts.join(' ');
}

/** 色の調整 → **盤の側の層だけ**に当てる CSS の filter（`boardGrayscale`。素のままなら ''） */
export function boardFilter(g) {
  if (!g || !(g.boardGrayscale > 0)) return '';
  return `grayscale(${Math.min(1, g.boardGrayscale)})`;
}

/**
 * **そのフレームのショット**（名前 ＋ 数値の上書き ＋ 揺れ）。ページと書き出しが同じ式を使う。
 * 揺れは位置と注視点を**同じだけ**盤の面（x / z）でずらす（向きを変えずに画面が震える。
 * カメラはどのショットも上から見下ろすので、高さ y を動かしても画面はほとんど動かない）。
 */
export function sceneShot(scene, base, shots = SHOTS) {
  const named = scene?.shot && shots[scene.shot] ? shots[scene.shot] : base;
  let shot = named;
  if (scene?.camera) {
    const { tilt, rise, push, ...nums } = scene.camera;
    shot = moveShot({ ...named, ...nums }, { tilt, rise, push });
  }
  const off = scene?.shakeOffset;
  if (!off) return shot;
  return { ...shot, x: shot.x + off[0], tx: (shot.tx ?? 0) + off[0], z: shot.z + off[1], tz: (shot.tz ?? 0) + off[1] };
}

const v3 = {
  sub: (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]],
  add: (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]],
  mul: (a, k) => [a[0] * k, a[1] * k, a[2] * k],
  cross: (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]],
  norm: (a) => { const l = Math.hypot(a[0], a[1], a[2]); return l > 1e-9 ? [a[0] / l, a[1] / l, a[2] / l] : null; },
};

/**
 * **ページのカメラからの相対の動き**（Issue #646。climax の `start` が初めて使う）。カメラの鍵のうち
 * `tilt`（度。正で見上げる＝カメラの位置を中心に、視線と上を「右」の軸まわりに回す）・
 * `rise`（ワールドの高さ y に足す）・`push`（盤の奥＝画面の上の向きへ水平に進める）は数値の上書きではなく、
 * **素のショット（ページのカメラ）を起点に**動かす。演出はページのカメラが何か知らないので、
 * 全部 0 に戻せば**どのカメラでも素のショットにぴったり着地する**（`reset` で 1 フレームも跳ばない）。
 * 奥の向きは視線と上の水平成分の和（実写のショットなら -z、90° 回転なら -x）。
 */
export function moveShot(shot, { tilt = 0, rise = 0, push = 0 } = {}) {
  const t = num(tilt); const r = num(rise); const p = num(push);
  if (!t && !r && !p) return shot;
  let pos = [num(shot.x), num(shot.y), num(shot.z)];
  let tgt = [num(shot.tx), num(shot.ty), num(shot.tz)];
  let up = Array.isArray(shot.up) ? [...shot.up] : [0, 1, 0];
  const f = v3.sub(tgt, pos);
  if (p) {
    const fwd = v3.norm([f[0] + up[0], 0, f[2] + up[2]]);
    if (fwd) { pos = v3.add(pos, v3.mul(fwd, p)); tgt = v3.add(tgt, v3.mul(fwd, p)); }
  }
  if (r) { pos = [pos[0], pos[1] + r, pos[2]]; tgt = [tgt[0], tgt[1] + r, tgt[2]]; }
  if (t) {
    const axis = v3.norm(v3.cross(f, up));
    if (axis) {
      const a = (t * Math.PI) / 180;
      const c = Math.cos(a); const s = Math.sin(a);
      // 軸は視線と上の両方に直交するので、ロドリゲスの式の (axis·v) の項は 0
      const rot = (v) => v3.add(v3.mul(v, c), v3.mul(v3.cross(axis, v), s));
      tgt = v3.add(pos, rot(f));
      up = rot(up);
    }
  }
  return { ...shot, x: pos[0], y: pos[1], z: pos[2], tx: tgt[0], ty: tgt[1], tz: tgt[2], up };
}
